import { useState, useCallback, useEffect } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import { useAuth } from '@/contexts/AuthContext';

const PREFIX = 'col-prefs:';
const PAGE_SIZE_PREFIX = 'page-size-prefs:';

function getStorageKey(username: string, tableId: string): string {
  return `${PREFIX}${username}:${tableId}`;
}

function getPageSizeKey(username: string, tableId: string): string {
  return `${PAGE_SIZE_PREFIX}${username}:${tableId}`;
}

export function useColumnPreferences(
  tableId: string,
  defaults: VisibilityState
): [VisibilityState, (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void] {
  const { user } = useAuth();
  const username = user?.username || 'anonymous';

  const [visibility, setVisibilityRaw] = useState<VisibilityState>(() => {
    try {
      const key = getStorageKey(username, tableId);
      const saved = localStorage.getItem(key);
      if (saved) {
        return { ...defaults, ...JSON.parse(saved) };
      }
    } catch { /* ignore */ }
    return defaults;
  });

  // Persist on change
  useEffect(() => {
    try {
      const key = getStorageKey(username, tableId);
      localStorage.setItem(key, JSON.stringify(visibility));
    } catch { /* ignore */ }
  }, [visibility, username, tableId]);

  const setVisibility = useCallback(
    (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
      setVisibilityRaw((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return next;
      });
    },
    []
  );

  return [visibility, setVisibility];
}

/** Persist page size per user per table */
export function usePageSizePreference(
  tableId: string,
  defaultSize: number = 10
): [number, (size: number) => void] {
  const { user } = useAuth();
  const username = user?.username || 'anonymous';

  const [pageSize, setPageSizeRaw] = useState<number>(() => {
    try {
      const key = getPageSizeKey(username, tableId);
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    } catch { /* ignore */ }
    return defaultSize;
  });

  useEffect(() => {
    try {
      const key = getPageSizeKey(username, tableId);
      localStorage.setItem(key, String(pageSize));
    } catch { /* ignore */ }
  }, [pageSize, username, tableId]);

  const setPageSize = useCallback((size: number) => {
    setPageSizeRaw(size);
  }, []);

  return [pageSize, setPageSize];
}

/** Get all saved column preferences for a given username */
export function getAllColumnPreferences(username: string): { tableId: string; hidden: string[]; pageSize?: number }[] {
  const results: { tableId: string; hidden: string[]; pageSize?: number }[] = [];
  const prefix = `${PREFIX}${username}:`;
  const pageSizePrefix = `${PAGE_SIZE_PREFIX}${username}:`;
  
  // Collect page sizes first
  const pageSizes: Record<string, number> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(pageSizePrefix)) {
      const tableId = key.slice(pageSizePrefix.length);
      try {
        const val = parseInt(localStorage.getItem(key) || '', 10);
        if (!isNaN(val)) pageSizes[tableId] = val;
      } catch { /* ignore */ }
    }
  }

  // Collect column prefs
  const seenTables = new Set<string>();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const tableId = key.slice(prefix.length);
      seenTables.add(tableId);
      try {
        const val = JSON.parse(localStorage.getItem(key) || '{}') as VisibilityState;
        const hidden = Object.entries(val)
          .filter(([, v]) => v === false)
          .map(([k]) => k);
        results.push({ tableId, hidden, pageSize: pageSizes[tableId] });
      } catch { /* ignore */ }
    }
  }

  // Add tables that only have page size prefs
  for (const [tableId, size] of Object.entries(pageSizes)) {
    if (!seenTables.has(tableId)) {
      results.push({ tableId, hidden: [], pageSize: size });
    }
  }

  return results.sort((a, b) => a.tableId.localeCompare(b.tableId));
}

/** Clear column and page size preferences for one or all tables */
export function clearColumnPreferences(username: string, tableId?: string): void {
  if (tableId) {
    localStorage.removeItem(getStorageKey(username, tableId));
    localStorage.removeItem(getPageSizeKey(username, tableId));
  } else {
    const prefixes = [`${PREFIX}${username}:`, `${PAGE_SIZE_PREFIX}${username}:`];
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  }
}
